package com.tixforgigs.common.core.util

import com.tixforgigs.apax.models.TrackEventAction
import com.tixforgigs.apax.models.TrackEventCategory
import com.tixforgigs.apax.models.TrackRequest
import com.tixforgigs.common.CommonBuildKonfig
import com.tixforgigs.common.core.enums.basePath
import com.tixforgigs.common.core.extensions.BuildKonfigUtil
import com.tixforgigs.common.core.util.AuthUtil.currentBearerToken
import com.tixforgigs.common.core.util.AuthUtil.deviceId
import com.tixforgigs.common.core.util.LocalizationUtil.translationLocaleAsString
import com.tixforgigs.common.http.HttpClient
import io.github.oshai.kotlinlogging.KotlinLogging
import io.github.oshai.kotlinlogging.logger
import io.ktor.http.*
import io.opentelemetry.kotlin.ExperimentalApi
import io.opentelemetry.kotlin.logging.model.SeverityNumber
import io.opentelemetry.kotlin.semconv.*
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import web.blob.*
import web.dom.document
import web.http.BodyInit
import web.navigator.navigator
import kotlin.random.Random

@OptIn(ExperimentalApi::class, IncubatingApi::class)
actual object TrackerUtil {

  const val SITE_ID = 6

  val log by KotlinLogging.logger()
  val logger = Otel.logger

  actual suspend fun pageView() {
    log.debug { "pageView" }
    sendPing(createTrackRequest())
    sendPageView()
  }

  private fun sendPageView() {
    CoroutineScope(Dispatchers.Unconfined).launch {
      val url = document.location.href
      val origin = IdHelper.origin ?: window.location.origin
      val templateId = IdHelper.templateId.toString()
      val fullPath = window.location.pathname
      val templateRelativePath = fullPath.toTemplateRelativePath()
      val deviceId = deviceId()
      val userId = currentUserId(deviceId)
      //val location = navigator.geolocation.getCurrentPosition().coords
      logger.emit(
        body = "${TrackEvent.PAGE_VIEW.value} $url",
        eventName = TrackEvent.PAGE_VIEW.name,
        severityNumber = SeverityNumber.INFO,
        severityText = SeverityNumber.INFO.name,
        attributes = {
          // Promote selected OTel attributes to Loki labels for dashboard aggregations.
          setStringAttribute(
            "loki.attribute.labels",
            "user.id,artifact.version,browser.language,origin,template_id,full_path,template_relative_path,flavor"
          )
          setStringAttribute(BrowserAttributes.BROWSER_LANGUAGE, translationLocaleAsString)
          setStringAttribute(ClientAttributes.CLIENT_ADDRESS, url)
          setStringAttribute(DeploymentAttributes.DEPLOYMENT_ENVIRONMENT_NAME, HttpClient.FLAVOR.value)
          setStringAttribute(DeviceAttributes.DEVICE_ID, deviceId)
          setStringAttribute(UserAttributes.USER_ID, userId)
          setStringAttribute(UserAgentAttributes.USER_AGENT_ORIGINAL, navigator.userAgent)
          setStringAttribute(ArtifactAttributes.ARTIFACT_VERSION, BuildKonfigUtil.CLIENT_VERSION)
          setStringAttribute("origin", origin)
          setStringAttribute("template_id", templateId)
          setStringAttribute("full_path", fullPath)
          setStringAttribute("template_relative_path", templateRelativePath)
          setStringAttribute("flavor", HttpClient.FLAVOR.value)
//          setStringAttribute(GeoAttributes.GEO_CONTINENT_CODE, "EU")
//          setStringAttribute(GeoAttributes.GEO_COUNTRY_ISO_CODE, "DE")
//          setStringAttribute(GeoAttributes.GEO_REGION_ISO_CODE, "DE-SN")
//          setStringAttribute(GeoAttributes.GEO_LOCALITY_NAME, "Leipzig")
//          setStringAttribute(GeoAttributes.GEO_POSTAL_CODE, "04100")
//          setDoubleAttribute(GeoAttributes.GEO_LOCATION_LAT, location.latitude)
//          setDoubleAttribute(GeoAttributes.GEO_LOCATION_LON, location.longitude)
        })
    }

  }

  private fun currentUserId(deviceId: String): String {
    return currentBearerToken.value?.accessToken
      ?.jwtSubjectOrNull()
      ?: deviceId
  }

  private fun String.jwtSubjectOrNull(): String? = runCatching {
    split(".")
      .getOrNull(1)
      ?.decodeBase64Url()
      ?.let(window::atob)
      ?.let(Json::parseToJsonElement)
      ?.jsonObject
      ?.get("sub")
      ?.jsonPrimitive
      ?.contentOrNull
  }.getOrNull()

  private fun String.decodeBase64Url(): String {
    val normalized = replace('-', '+').replace('_', '/')
    val remainder = normalized.length % 4
    return if (remainder == 0) normalized else normalized.padEnd(normalized.length + (4 - remainder), '=')
  }

  private fun String.toTemplateRelativePath(): String {
    val normalizedBasePath = basePath.trim('/').takeIf { it.isNotBlank() }
      ?.let { "/$it" }
    val relativePath = normalizedBasePath
      ?.takeIf { startsWith(it) }
      ?.let { removePrefix(it) }
      ?: this
    return relativePath.takeIf { it.startsWith("/") && it.isNotBlank() }
      ?: "/"
  }

  actual suspend fun trackEvent(category: TrackEventCategory, action: TrackEventAction, name: String) {
    sendPing(
      createTrackRequest(
        ca = 1,
        eC = category,
        eA = action,
        eN = name,
      )
    )
  }


  private suspend fun sendPing(data: TrackRequest) = navigator.sendBeacon(
    url = CommonBuildKonfig.MATOMO_URL,
    data = BodyInit(
      value = Blob(
        arrayOf(data.toFormDataContent().formData.formUrlEncode()),
        BlobPropertyBag.Companion(EndingType.native, ContentType.Application.FormUrlEncoded.toString())
      ).arrayBuffer(),
    )
  )

  private fun createTrackRequest(
    eC: TrackEventCategory? = null,
    eA: TrackEventAction? = null,
    eN: String? = null,
    ca: Int? = null,
  ) = TrackRequest(
    idsite = SITE_ID,
    apiv = 1,
    rec = 1,
    sendImage = 0,
    ca = ca,
    eC = eC,
    eA = eA,
    eN = eN,
    rand = Random.nextInt(111111, 999999),
    actionName = document.title,
    url = document.location.href
  )
}

enum class TrackEvent(val value: String) {
  PAGE_VIEW("page.view"),
  CART_VIEW("cart.view"),
  PAYMENT_START("payment.start"),
  PAYMENT_SUCCESS("payment.success")
}
